import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { admin } from '@/lib/api';

const Stat = ({ label, value }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-semibold">{value}</div>
    </CardContent>
  </Card>
);

const QueryTable = ({ title, rows, empty }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      {rows?.length ? (
        <ul className="space-y-2 text-sm">
          {rows.map((row) => (
            <li key={row.query} className="flex justify-between gap-4 border-b pb-2">
              <span className="truncate">{row.query}</span>
              <span className="shrink-0 font-medium">{row.count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{empty}</p>
      )}
    </CardContent>
  </Card>
);

const AiSearchTab = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [barcode, setBarcode] = useState('');
  const [matches, setMatches] = useState([]);
  const [matching, setMatching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    admin
      .aiSearchStats()
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(t('search.aiAdminError', 'İstatistik okunamadı.'));
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const runMatch = async (event) => {
    event.preventDefault();
    setMatching(true);
    try {
      const res = await admin.matchProducts({ name, barcode });
      setMatches(res.data?.matches || []);
    } catch {
      setMatches([]);
    } finally {
      setMatching(false);
    }
  };

  const rate = stats ? `${Math.round((stats.success_rate || 0) * 100)}%` : '—';

  return (
    <div className="space-y-6" data-testid="ai-search-admin">
      <div>
        <h1 className="text-2xl font-semibold">{t('search.aiAdminTitle', 'AI arama')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {stats?.llm_enabled
            ? t('search.aiAdminModelOn', 'Yerel model açık')
            : t('search.aiAdminModelOff', 'Yerel model kapalı')}
          {' · '}
          {t('search.aiAdminVector', 'vektör')} {stats?.embed_mode || 'hash'}
        </p>
        {error ? <p className="text-sm text-red-600 mt-2">{error}</p> : null}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat label={t('search.aiAdminSearches', 'Arama')} value={stats?.searches ?? '—'} />
        <Stat label={t('search.aiAdminSuccess', 'Başarı')} value={rate} />
        <Stat label={t('search.aiAdminZero', 'Boş sonuç')} value={stats?.zero_results ?? '—'} />
        <Stat label={t('search.aiAdminClicks', 'Tıklama')} value={stats?.clicks ?? '—'} />
        <Stat label={t('search.aiAdminCart', 'Sepet')} value={stats?.add_to_cart ?? '—'} />
        <Stat label={t('search.aiAdminPurchases', 'Satın alma')} value={stats?.purchases ?? '—'} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <QueryTable
          title={t('search.aiAdminTop', 'En çok sorulanlar')}
          rows={stats?.top_queries}
          empty={t('search.aiAdminEmpty', 'Henüz kayıt yok.')}
        />
        <QueryTable
          title={t('search.aiAdminZeroQueries', 'Sonuç vermeyenler')}
          rows={stats?.zero_queries}
          empty={t('search.aiAdminEmpty', 'Henüz kayıt yok.')}
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('search.aiAdminMatch', 'Ürün eşleştirme')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={runMatch} className="flex flex-col sm:flex-row gap-2">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('search.aiAdminName', 'Ürün adı')}
              data-testid="ai-match-name"
            />
            <Input
              value={barcode}
              onChange={(event) => setBarcode(event.target.value)}
              placeholder={t('search.aiAdminBarcode', 'Barkod')}
              data-testid="ai-match-barcode"
            />
            <Button type="submit" disabled={matching || (!name.trim() && !barcode.trim())}>
              {t('search.aiAdminMatchBtn', 'Eşleştir')}
            </Button>
          </form>
          <ul className="mt-4 space-y-2 text-sm">
            {matches.map((item) => (
              <li key={item.id || item.name} className="flex justify-between gap-3 border-b pb-2">
                <span className="truncate">{item.name}</span>
                <span className="shrink-0">
                  {item.reason} · {item.score}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AiSearchTab;
